import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { Provider, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

type AuthResult = {
  data: unknown;
  error: { message: string } | null;
};

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signUp: (email: string, password: string) => Promise<AuthResult>;
  requestPasswordReset: (email: string) => Promise<AuthResult>;
  updatePassword: (password: string) => Promise<AuthResult>;
  signInWithProvider: (provider: Provider) => Promise<AuthResult>;
  signOut: () => Promise<{ error: { message: string } | null }>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const E2E_AUTH_USER_KEY = 'taccounts_e2e_auth_user';

function resolveLocalE2eUser(): User | null {
  if (window.location.hostname !== 'localhost') {
    return null;
  }

  const raw = window.localStorage.getItem(E2E_AUTH_USER_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<User>;
    if (!parsed?.id || typeof parsed.id !== 'string') {
      return null;
    }

    return parsed as User;
  } catch {
    return null;
  }
}

function toAuthError(error: unknown): { message: string } {
  if (error instanceof Error) {
    const normalized = error.message.trim().toLowerCase();

    if (
      normalized.includes('suspended')
      || normalized.includes('paused')
      || normalized.includes('project is disabled')
      || normalized.includes('project has been paused')
    ) {
      return {
        message: 'Authentication is temporarily unavailable because the Supabase project is suspended. Please reactivate the project and try again.',
      };
    }

    return { message: error.message };
  }

  return { message: 'Authentication request failed. Please try again.' };
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Start as true while checking initial auth
  const authRedirectUrl = window.location.origin;

  useEffect(() => {
    const localE2eUser = resolveLocalE2eUser();
    if (localE2eUser) {
      setUser(localE2eUser);
      setLoading(false);
      return () => {
        // No live subscription in e2e local override mode.
      };
    }

    // Check initial session
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
      } catch (error) {
        console.warn('Supabase auth not configured');
      } finally {
        setLoading(false); // Auth check complete
      }
    };

    initializeAuth();
    
    // Listen for changes on auth state (logged in, signed out, etc.)
    let subscription: { unsubscribe: () => void } | null = null;
    try {
      const { data } = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user ?? null);
      });
      subscription = data.subscription;
    } catch (error) {
      // Silently fail - app works without Supabase for free accounts
      console.warn('Supabase auth not configured');
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // Sign in with email and password
  const signIn = async (email: string, password: string): Promise<AuthResult> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { data, error };
    } catch (error) {
      return { data: null, error: toAuthError(error) };
    }
  };

  // Sign up with email and password
  const signUp = async (email: string, password: string): Promise<AuthResult> => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: authRedirectUrl,
        },
      });
      return { data, error };
    } catch (error) {
      return { data: null, error: toAuthError(error) };
    }
  };

  // Send password reset email
  const requestPasswordReset = async (email: string): Promise<AuthResult> => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${authRedirectUrl}/auth?mode=reset`,
      });
      return { data, error };
    } catch (error) {
      return { data: null, error: toAuthError(error) };
    }
  };

  // Update current user's password (used in recovery flow)
  const updatePassword = async (password: string): Promise<AuthResult> => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password,
      });
      return { data, error };
    } catch (error) {
      return { data: null, error: toAuthError(error) };
    }
  };

  // Sign in with OAuth provider
  const signInWithProvider = async (provider: Provider): Promise<AuthResult> => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: authRedirectUrl,
        },
      });
      return { data, error };
    } catch (error) {
      return { data: null, error: toAuthError(error) };
    }
  };

  // Sign out
  const signOut = async (): Promise<{ error: { message: string } | null }> => {
    try {
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      return { error: toAuthError(error) };
    }
  };

  const value: AuthContextValue = {
    user,
    loading,
    signIn,
    signUp,
    requestPasswordReset,
    updatePassword,
    signInWithProvider,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
